// src/stats.ts
function mean(values) {
  if (values.length === 0) return null;
  let total = 0;
  for (const value of values) total += value;
  return total / values.length;
}
var percentile = (values, fraction) => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * fraction;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const start = sorted[lower];
  if (lower === upper) return start;
  return start + (sorted[upper] - start) * (position - lower);
};
var median = (values) => percentile(values, 0.5);
function sampleStdDev(values) {
  if (values.length < 2) return null;
  const average = mean(values);
  if (average === null) return null;
  let squares = 0;
  for (const value of values) squares += (value - average) ** 2;
  return Math.sqrt(squares / (values.length - 1));
}
var coefficientOfVariation = (values) => {
  const average = mean(values);
  const deviation = sampleStdDev(values);
  if (average === null || deviation === null || average === 0) return null;
  return deviation / Math.abs(average);
};
function hitRate(values, line, side) {
  const total = values.length;
  let hits = 0;
  for (const value of values) {
    if (side === "over" ? value > line : value < line) hits += 1;
  }
  return { hits, total, rate: total === 0 ? null : hits / total };
}

// src/ev.ts
function expectedValue(probability, decimalOdds) {
  if (!(probability >= 0 && probability <= 1)) {
    throw new RangeError("probability must be between 0 and 1");
  }
  if (!(decimalOdds > 1)) {
    throw new RangeError("decimal odds must be greater than 1");
  }
  return probability * decimalOdds - 1;
}

// src/radar.ts
var object = (value) => value !== null && typeof value === "object" && !Array.isArray(value) ? value : {};
var percentage = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 100 ? value / 100 : null;
var radarSelections = (input) => input.flatMap((raw) => {
  const row = object(raw);
  const type = object(row.type).developer_name;
  const values = object(row.predictions);
  if (typeof type !== "string") return [];
  const team = /^(HOME|AWAY)_OVER_UNDER_(\d+(?:_\d+)?)_PROBABILITY$/.exec(type);
  const total = team ?? /^(CORNERS_)?OVER_UNDER_(\d+(?:_\d+)?)_PROBABILITY$/.exec(type);
  const market = total ? total[1] === "CORNERS_" ? "corners" : "goals" : type === "BTTS_PROBABILITY" ? "btts" : ["FULLTIME_RESULT_PROBABILITY", "FIRST_HALF_WINNER_PROBABILITY"].includes(type) ? "result" : null;
  if (market === null) return [];
  const line = total ? Number(total[2].replace("_", ".")) : null;
  const push = line !== null && Number.isInteger(line) ? percentage(values.equal) : 0;
  if (push === null) return [];
  const sides = total ? ["over", "under"] : market === "btts" ? ["yes", "no"] : ["home", "draw", "away"];
  const probabilities = sides.map(
    (side) => percentage(values[side === "over" ? "yes" : side === "under" ? "no" : side])
  );
  if (probabilities.some((value) => value === null) || Math.abs(probabilities.reduce((sum2, value) => sum2 + (value ?? 0), push) - 1) > 2e-3)
    return [];
  const teamSide = team ? team[1] === "HOME" ? "home" : "away" : null;
  return sides.map((side, index) => ({
    market,
    period: type === "FIRST_HALF_WINNER_PROBABILITY" ? "first_half" : "full",
    ...teamSide ? { teamSide } : {},
    side,
    line,
    probability: probabilities[index],
    pushProbability: push
  }));
});
var radarValueBet = (selection, price) => {
  if (!Number.isFinite(price) || price <= 1 || selection.probability <= 0) return null;
  return {
    expectedValue: Number(
      (selection.probability * price - (1 - selection.pushProbability)).toFixed(10)
    ),
    fairOdds: (1 - selection.pushProbability) / selection.probability
  };
};

// src/market-key.ts
var METRICS = [
  "goals",
  "corners",
  "cards",
  "shots",
  "shots_on_target",
  "fouls",
  "result",
  "btts"
];
var PERIODS = ["full", "first_half", "second_half"];
var MARKET_SIDES = ["over", "under", "home", "draw", "away", "yes", "no"];
var LINE_METRICS = /* @__PURE__ */ new Set([
  "goals",
  "corners",
  "cards",
  "shots",
  "shots_on_target",
  "fouls"
]);
var SIDES_BY_METRIC = {
  goals: ["over", "under"],
  corners: ["over", "under"],
  cards: ["over", "under"],
  shots: ["over", "under"],
  shots_on_target: ["over", "under"],
  fouls: ["over", "under"],
  result: ["home", "draw", "away"],
  btts: ["yes", "no"]
};
function isMetric(value) {
  return METRICS.includes(value);
}
function isSide(value) {
  return MARKET_SIDES.includes(value);
}
function parseMarketKey(key) {
  const parts = key.split("_");
  let metric;
  let rest;
  if (parts[0] === "shots" && parts[1] === "on" && parts[2] === "target") {
    metric = "shots_on_target";
    rest = parts.slice(3);
  } else {
    metric = parts[0];
    rest = parts.slice(1);
  }
  if (metric === void 0 || !isMetric(metric))
    throw new Error(`unknown metric in market key ${key}`);
  let period = "full";
  if (rest[0] === "ht") {
    period = "first_half";
    rest = rest.slice(1);
  }
  const side = rest[0];
  if (side === void 0 || !isSide(side) || !SIDES_BY_METRIC[metric].includes(side)) {
    throw new Error(`invalid side in market key ${key}`);
  }
  const lineText = rest[1];
  if (LINE_METRICS.has(metric)) {
    if (lineText === void 0 || rest.length !== 2)
      throw new Error(`missing line in market key ${key}`);
    const line = Number(lineText);
    if (!Number.isFinite(line)) throw new Error(`invalid line in market key ${key}`);
    return { metric, period, side, line };
  }
  if (rest.length !== 1) throw new Error(`unexpected line in market key ${key}`);
  return { metric, period, side, line: null };
}
function formatMarketKey(key) {
  const periodPart = key.period === "first_half" ? "_ht" : "";
  const linePart = key.line === null ? "" : `_${key.line}`;
  return `${key.metric}${periodPart}_${key.side}${linePart}`;
}

// src/sample.ts
function selectSample(rows, filters) {
  const candidates = rows.filter((row) => row.isFinished).filter((row) => filters.venue === "all" || row.venue === filters.venue).filter((row) => filters.competitionId === null || row.competitionId === filters.competitionId).sort((a, b) => b.kickoffAt.getTime() - a.kickoffAt.getTime());
  let excludedMissing = 0;
  const eligible = [];
  for (const row of candidates) {
    const value = row.values[filters.metric];
    if (value === null || value === void 0) {
      excludedMissing += 1;
      continue;
    }
    eligible.push(row);
  }
  const selected = eligible.slice(0, filters.size);
  const newest = selected[0];
  const oldest = selected[selected.length - 1];
  return {
    rows: selected,
    eligibleCount: eligible.length,
    excludedMissing,
    dateRange: newest && oldest ? { from: oldest.kickoffAt, to: newest.kickoffAt } : null
  };
}

// src/projection.ts
function teamProjection(ownFor, opponentAgainst) {
  if (ownFor === null || opponentAgainst === null) return null;
  return (ownFor + opponentAgainst) / 2;
}
function matchProjection(home, away) {
  if (home === null || away === null) return null;
  return home + away;
}

// src/sport.ts
var SPORTS = ["football", "nba", "nfl", "mlb"];
var isSport = (value) => typeof value === "string" && SPORTS.includes(value);

// src/sports/types.ts
var SAMPLE_SIZES = [5, 10, 20];

// src/sports/football.ts
var FOOTBALL = {
  sport: "football",
  periods: ["full", "first_half", "second_half"],
  teamMetrics: [
    { key: "goals", countable: true },
    { key: "corners", countable: true },
    { key: "cards", countable: true },
    { key: "shots", countable: true },
    { key: "shots_on_target", countable: true },
    { key: "shots_off_target", countable: true },
    { key: "shots_inside_box", countable: true },
    { key: "shots_outside_box", countable: true },
    { key: "shots_blocked", countable: true },
    { key: "attacks", countable: true },
    { key: "dangerous_attacks", countable: true },
    { key: "passes", countable: true },
    { key: "accurate_passes", countable: true },
    { key: "tackles", countable: true },
    { key: "interceptions", countable: true },
    { key: "crosses", countable: true },
    { key: "accurate_crosses", countable: true },
    { key: "big_chances_created", countable: true },
    { key: "key_passes", countable: true },
    { key: "fouls", countable: true },
    { key: "yellow_cards", countable: true },
    { key: "red_cards", countable: true },
    { key: "offsides", countable: true },
    { key: "saves", countable: true },
    { key: "possession", countable: false },
    { key: "hit_woodwork", countable: true },
    { key: "penalty_kicks", countable: true },
    { key: "duels_won", countable: true },
    { key: "dribbles", countable: true },
    { key: "big_chances_missed", countable: true },
    { key: "tackles_won", countable: true },
    { key: "throw_ins", countable: true },
    { key: "goal_kicks", countable: true },
    { key: "free_kicks", countable: true },
    { key: "long_passes", countable: true },
    { key: "xg", countable: false },
    { key: "goals_0_15", countable: true },
    { key: "goals_15_30", countable: true },
    { key: "goals_30_45", countable: true },
    { key: "goals_45_60", countable: true },
    { key: "goals_60_75", countable: true },
    { key: "goals_75_90", countable: true }
  ],
  playerMetrics: [
    { key: "fouls_drawn", countable: true },
    { key: "crosses", countable: true },
    { key: "accurate_crosses", countable: true },
    { key: "aerials_won", countable: true },
    { key: "shots_off_target", countable: true },
    { key: "goals", countable: true },
    { key: "assists", countable: true },
    { key: "shots", countable: true },
    { key: "shots_on_target", countable: true },
    { key: "passes", countable: true },
    { key: "accurate_passes", countable: true },
    { key: "key_passes", countable: true },
    { key: "dribbles", countable: true },
    { key: "duels_won", countable: true },
    { key: "tackles", countable: true },
    { key: "interceptions", countable: true },
    { key: "clearances", countable: true },
    { key: "saves", countable: true },
    { key: "touches", countable: true },
    { key: "fouls", countable: true },
    { key: "yellow_cards", countable: true },
    { key: "red_cards", countable: true },
    { key: "minutes", countable: true },
    { key: "rating", countable: false },
    { key: "tackles_won", countable: true },
    { key: "blocked_shots", countable: true },
    { key: "dispossessed", countable: true },
    { key: "duels", countable: true },
    { key: "big_chances_created", countable: true },
    { key: "big_chances_missed", countable: true },
    { key: "penalties_committed", countable: true },
    { key: "goals_conceded", countable: true },
    { key: "own_goals", countable: true },
    { key: "offsides", countable: true },
    { key: "hit_woodwork", countable: true },
    { key: "xg", countable: false }
  ],
  markets: [
    { key: "result", scope: "match", metric: "goals" },
    { key: "goals", scope: "match", metric: "goals" },
    { key: "corners", scope: "match", metric: "corners" },
    { key: "btts", scope: "match", metric: "goals" },
    { key: "cards", scope: "team", metric: "cards" },
    { key: "shots", scope: "team", metric: "shots" },
    { key: "shots_on_target", scope: "team", metric: "shots_on_target" }
  ],
  propMetrics: [
    { propType: "goals", metric: "goals", milestone: true },
    { propType: "assists", metric: "assists", milestone: true },
    { propType: "shots", metric: "shots", milestone: true },
    { propType: "shots_on_target", metric: "shots_on_target", milestone: true },
    { propType: "tackles", metric: "tackles", milestone: true },
    { propType: "fouls", metric: "fouls", milestone: true },
    { propType: "yellow_cards", metric: "yellow_cards", milestone: true }
  ],
  sampleSizes: SAMPLE_SIZES
};

// src/sports/mlb.ts
var MLB = {
  sport: "mlb",
  periods: ["full"],
  teamMetrics: [
    { key: "runs", countable: true },
    { key: "hits", countable: true },
    { key: "errors", countable: true }
  ],
  playerMetrics: [
    { key: "plate_appearances", countable: true },
    { key: "total_bases", countable: true },
    { key: "pitches_thrown", countable: true },
    { key: "at_bats", countable: true },
    { key: "hits", countable: true },
    { key: "runs", countable: true },
    { key: "rbi", countable: true },
    { key: "home_runs", countable: true },
    { key: "walks", countable: true },
    { key: "strikeouts", countable: true },
    { key: "doubles", countable: true },
    { key: "triples", countable: true },
    { key: "stolen_bases", countable: true },
    { key: "batting_average", countable: false },
    { key: "on_base_percentage", countable: false },
    { key: "slugging", countable: false },
    { key: "innings_pitched", countable: true },
    { key: "earned_run_average", countable: false },
    { key: "pitching_strikeouts", countable: true },
    { key: "pitching_walks", countable: true },
    { key: "saves", countable: true },
    { key: "errors", countable: true },
    { key: "singles", countable: true },
    { key: "extra_base_hits", countable: true },
    { key: "hits_runs_rbis", countable: true },
    { key: "runs_rbis", countable: true },
    { key: "hits_stolen_bases", countable: true },
    { key: "hits_runs_stolen_bases", countable: true },
    { key: "hits_walks_stolen_bases", countable: true },
    { key: "pitcher_outs", countable: true },
    { key: "pitcher_earned_runs", countable: true },
    { key: "pitcher_hits_allowed", countable: true },
    { key: "pitcher_hits_walks_earned_runs", countable: true },
    { key: "pitcher_win", countable: true },
    { key: "first_home_run", countable: true }
  ],
  markets: [
    { key: "moneyline", scope: "team", metric: "runs" },
    { key: "spread", scope: "team", metric: "runs" },
    { key: "runs", scope: "match", metric: "runs" }
  ],
  propMetrics: [
    { propType: "hits", metric: "hits", milestone: true },
    { propType: "singles", metric: "singles", milestone: true },
    { propType: "doubles", metric: "doubles", milestone: true },
    { propType: "triples", metric: "triples", milestone: true },
    { propType: "home_runs", metric: "home_runs", milestone: true },
    { propType: "total_bases", metric: "total_bases", milestone: true },
    { propType: "extra_base_hits", metric: "extra_base_hits", milestone: true },
    { propType: "runs", metric: "runs", milestone: true },
    { propType: "runs_scored", metric: "runs", milestone: true },
    { propType: "rbi", metric: "rbi", milestone: true },
    { propType: "rbis", metric: "rbi", milestone: true },
    { propType: "walks", metric: "walks", milestone: true },
    { propType: "strikeouts", metric: "strikeouts", milestone: true },
    { propType: "stolen_bases", metric: "stolen_bases", milestone: true },
    { propType: "hits_runs_rbis", metric: "hits_runs_rbis", milestone: true },
    { propType: "runs_rbis", metric: "runs_rbis", milestone: true },
    { propType: "hits_stolen_bases", metric: "hits_stolen_bases", milestone: true },
    {
      propType: "hits_runs_stolen_bases",
      metric: "hits_runs_stolen_bases",
      milestone: true
    },
    {
      propType: "hits_walks_stolen_bases",
      metric: "hits_walks_stolen_bases",
      milestone: true
    },
    { propType: "first_home_run", metric: "first_home_run", milestone: false },
    { propType: "pitcher_strikeouts", metric: "pitching_strikeouts", milestone: true },
    { propType: "pitcher_outs", metric: "pitcher_outs", milestone: true },
    { propType: "outs", metric: "pitcher_outs", milestone: true },
    { propType: "pitcher_earned_runs", metric: "pitcher_earned_runs", milestone: true },
    { propType: "pitcher_hits_allowed", metric: "pitcher_hits_allowed", milestone: true },
    { propType: "pitcher_walks", metric: "pitching_walks", milestone: true },
    { propType: "pitcher_win", metric: "pitcher_win", milestone: false },
    { propType: "pitcher_record_a_win", metric: "pitcher_win", milestone: false },
    {
      propType: "hits_allowed_walks_allowed_earned_runs_allowed",
      metric: "pitcher_hits_walks_earned_runs",
      milestone: true
    }
  ],
  sampleSizes: SAMPLE_SIZES
};

// src/sports/nba.ts
var NBA = {
  sport: "nba",
  periods: ["full", "q1", "q2", "q3", "q4", "ot"],
  teamMetrics: [
    { key: "points", countable: true },
    { key: "rebounds", countable: true },
    { key: "offensive_rebounds", countable: true },
    { key: "defensive_rebounds", countable: true },
    { key: "assists", countable: true },
    { key: "steals", countable: true },
    { key: "blocks", countable: true },
    { key: "turnovers", countable: true },
    { key: "fouls", countable: true },
    { key: "field_goals_made", countable: true },
    { key: "field_goals_attempted", countable: true },
    { key: "three_pointers_made", countable: true },
    { key: "three_pointers_attempted", countable: true },
    { key: "free_throws_made", countable: true },
    { key: "free_throws_attempted", countable: true }
  ],
  playerMetrics: [
    { key: "usage_percentage", countable: false },
    { key: "true_shooting_percentage", countable: false },
    { key: "effective_field_goal_percentage", countable: false },
    { key: "offensive_rating", countable: false },
    { key: "defensive_rating", countable: false },
    { key: "net_rating", countable: false },
    { key: "pace", countable: false },
    { key: "pie", countable: false },
    { key: "points", countable: true },
    { key: "rebounds", countable: true },
    { key: "offensive_rebounds", countable: true },
    { key: "defensive_rebounds", countable: true },
    { key: "assists", countable: true },
    { key: "steals", countable: true },
    { key: "blocks", countable: true },
    { key: "turnovers", countable: true },
    { key: "fouls", countable: true },
    { key: "minutes", countable: true },
    { key: "field_goals_made", countable: true },
    { key: "field_goals_attempted", countable: true },
    { key: "three_pointers_made", countable: true },
    { key: "three_pointers_attempted", countable: true },
    { key: "free_throws_made", countable: true },
    { key: "free_throws_attempted", countable: true },
    { key: "plus_minus", countable: false },
    { key: "points_rebounds_assists", countable: true },
    { key: "points_rebounds", countable: true },
    { key: "points_assists", countable: true },
    { key: "rebounds_assists", countable: true },
    { key: "double_double", countable: true },
    { key: "triple_double", countable: true }
  ],
  markets: [
    { key: "moneyline", scope: "team", metric: "points" },
    { key: "spread", scope: "team", metric: "points" },
    { key: "points", scope: "match", metric: "points" }
  ],
  propMetrics: [
    { propType: "points", metric: "points", milestone: true },
    { propType: "rebounds", metric: "rebounds", milestone: true },
    { propType: "assists", metric: "assists", milestone: true },
    { propType: "three_pointers_made", metric: "three_pointers_made", milestone: true },
    { propType: "threes", metric: "three_pointers_made", milestone: true },
    { propType: "steals", metric: "steals", milestone: true },
    { propType: "blocks", metric: "blocks", milestone: true },
    { propType: "turnovers", metric: "turnovers", milestone: true },
    {
      propType: "points_rebounds_assists",
      metric: "points_rebounds_assists",
      milestone: true
    },
    { propType: "points_rebounds", metric: "points_rebounds", milestone: true },
    { propType: "points_assists", metric: "points_assists", milestone: true },
    { propType: "rebounds_assists", metric: "rebounds_assists", milestone: true },
    { propType: "double_double", metric: "double_double", milestone: false },
    { propType: "triple_double", metric: "triple_double", milestone: false }
  ],
  sampleSizes: SAMPLE_SIZES
};

// src/sports/nfl.ts
var NFL = {
  sport: "nfl",
  periods: ["full", "q1", "q2", "q3", "q4", "ot"],
  teamMetrics: [
    { key: "points", countable: true },
    { key: "total_yards", countable: true },
    { key: "passing_yards", countable: true },
    { key: "rushing_yards", countable: true },
    { key: "passing_attempts", countable: true },
    { key: "passing_completions", countable: true },
    { key: "rushing_attempts", countable: true },
    { key: "first_downs", countable: true },
    { key: "first_downs_passing", countable: true },
    { key: "first_downs_rushing", countable: true },
    { key: "first_downs_penalty", countable: true },
    { key: "third_down_conversions", countable: true },
    { key: "third_down_attempts", countable: true },
    { key: "fourth_down_conversions", countable: true },
    { key: "fourth_down_attempts", countable: true },
    { key: "red_zone_scores", countable: true },
    { key: "red_zone_attempts", countable: true },
    { key: "turnovers", countable: true },
    { key: "interceptions_thrown", countable: true },
    { key: "fumbles_lost", countable: true },
    { key: "penalties", countable: true },
    { key: "penalty_yards", countable: true },
    { key: "sacks", countable: true },
    { key: "sack_yards_lost", countable: true },
    { key: "total_offensive_plays", countable: true },
    { key: "total_drives", countable: true },
    { key: "possession_time_seconds", countable: true },
    { key: "defensive_touchdowns", countable: true },
    { key: "yards_per_play", countable: false },
    { key: "yards_per_pass", countable: false },
    { key: "yards_per_rush_attempt", countable: false }
  ],
  playerMetrics: [
    { key: "passing_yards", countable: true },
    { key: "passing_touchdowns", countable: true },
    { key: "passing_completions", countable: true },
    { key: "passing_attempts", countable: true },
    { key: "passing_interceptions", countable: true },
    { key: "rushing_yards", countable: true },
    { key: "rushing_touchdowns", countable: true },
    { key: "rushing_attempts", countable: true },
    { key: "receiving_yards", countable: true },
    { key: "receiving_touchdowns", countable: true },
    { key: "receptions", countable: true },
    { key: "receiving_targets", countable: true },
    { key: "total_tackles", countable: true },
    { key: "solo_tackles", countable: true },
    { key: "defensive_sacks", countable: true },
    { key: "defensive_interceptions", countable: true },
    { key: "anytime_touchdown", countable: true },
    { key: "first_touchdown", countable: true },
    { key: "rushing_receiving_yards", countable: true },
    { key: "longest_reception", countable: false },
    { key: "longest_rush", countable: false },
    { key: "longest_pass", countable: false },
    { key: "kicking_points", countable: true },
    { key: "field_goals_made", countable: true }
  ],
  markets: [
    { key: "moneyline", scope: "team", metric: "points" },
    { key: "spread", scope: "team", metric: "points" },
    { key: "points", scope: "match", metric: "points" }
  ],
  propMetrics: [
    { propType: "passing_yards", metric: "passing_yards", milestone: true },
    { propType: "passing_touchdowns", metric: "passing_touchdowns", milestone: true },
    { propType: "passing_tds", metric: "passing_touchdowns", milestone: true },
    { propType: "passing_completions", metric: "passing_completions", milestone: true },
    { propType: "passing_attempts", metric: "passing_attempts", milestone: true },
    { propType: "passing_interceptions", metric: "passing_interceptions", milestone: true },
    { propType: "interceptions", metric: "passing_interceptions", milestone: true },
    { propType: "rushing_yards", metric: "rushing_yards", milestone: true },
    { propType: "rushing_attempts", metric: "rushing_attempts", milestone: true },
    { propType: "receiving_yards", metric: "receiving_yards", milestone: true },
    { propType: "receptions", metric: "receptions", milestone: true },
    {
      propType: "rushing_receiving_yards",
      metric: "rushing_receiving_yards",
      milestone: true
    },
    { propType: "anytime_touchdown", metric: "anytime_touchdown", milestone: false },
    { propType: "anytime_td", metric: "anytime_touchdown", milestone: false },
    { propType: "first_touchdown", metric: "first_touchdown", milestone: false },
    { propType: "first_td", metric: "first_touchdown", milestone: false },
    { propType: "longest_reception", metric: "longest_reception", milestone: true },
    { propType: "longest_rush", metric: "longest_rush", milestone: true },
    { propType: "longest_pass", metric: "longest_pass", milestone: true },
    { propType: "kicking_points", metric: "kicking_points", milestone: true },
    { propType: "field_goals_made", metric: "field_goals_made", milestone: true },
    { propType: "fg_made", metric: "field_goals_made", milestone: true }
  ],
  sampleSizes: SAMPLE_SIZES
};

// src/sports/index.ts
var SPORT_DEFINITIONS = {
  football: FOOTBALL,
  nba: NBA,
  nfl: NFL,
  mlb: MLB
};
var sportDefinition = (sport) => {
  const definition = SPORT_DEFINITIONS[sport];
  if (!definition) throw new Error(`unknown sport ${sport}`);
  return definition;
};
var teamMetricKeys = (sport) => sportDefinition(sport).teamMetrics.map((metric) => metric.key);
var playerMetricKeys = (sport) => sportDefinition(sport).playerMetrics.map((metric) => metric.key);
var findMetric = (metrics, metric) => metrics.find((candidate) => candidate.key === metric);
var isTeamMetric = (sport, metric) => findMetric(sportDefinition(sport).teamMetrics, metric) !== void 0;
var isPlayerMetric = (sport, metric) => findMetric(sportDefinition(sport).playerMetrics, metric) !== void 0;
var isPeriod = (sport, period) => sportDefinition(sport).periods.includes(period);
var isCountableTeamMetric = (sport, metric) => findMetric(sportDefinition(sport).teamMetrics, metric)?.countable === true;
var isCountablePlayerMetric = (sport, metric) => findMetric(sportDefinition(sport).playerMetrics, metric)?.countable === true;
var PERIOD_SUFFIX = /_(1q|2q|3q|4q|1h|2h)$/;
var PERIOD_INFIX = /_1st_\d+_innings?$/;
var UNSETTLEABLE = ["plate_appearance_pitch_count"];
var FOOTBALL_RADAR_MARKET_KEYS = [
  "result",
  "goals",
  "corners",
  "cards",
  "btts",
  "assists",
  "shots",
  "shots_on_target",
  "tackles",
  "fouls",
  "yellow_cards"
];
var sportMarkets = (sport) => sportDefinition(sport).markets;
var marketMetric = (sport, market) => {
  const declared = sportDefinition(sport).markets.find((entry) => entry.key === market);
  if (declared !== void 0) return declared.metric;
  return isTeamMetric(sport, market) ? market : null;
};
var isRejectedPropType = (propType) => PERIOD_SUFFIX.test(propType) || PERIOD_INFIX.test(propType) || UNSETTLEABLE.includes(propType);
var propMetricFor = (sport, propType) => {
  if (isRejectedPropType(propType)) return null;
  const found = sportDefinition(sport).propMetrics.find((prop) => prop.propType === propType);
  return found ? { metric: found.metric, milestone: found.milestone } : null;
};
var milestoneLine = (line, milestone) => milestone && Number.isInteger(line) ? line - 0.5 : line;
var playerPropMetricKeys = (sport) => [
  ...new Set(sportDefinition(sport).propMetrics.map((prop) => prop.metric))
];
var radarMarketKeys = (sport) => sport === "football" ? FOOTBALL_RADAR_MARKET_KEYS : [
  .../* @__PURE__ */ new Set([
    ...sportMarkets(sport).map((market) => market.key),
    ...playerPropMetricKeys(sport)
  ])
];

// src/availability.ts
var AVAILABILITY_STATUSES = [
  "out",
  "doubtful",
  "questionable",
  "day_to_day",
  "probable",
  "active"
];
var OUT_CODES = ["out", "ir", "pup", "nfi"];
var normalizeAvailability = (raw) => {
  const text = raw.trim().toLowerCase().replace(/[\s_]+/g, "-");
  if (text === "doubtful") return "doubtful";
  if (text === "questionable" || text === "gtd") return "questionable";
  if (text === "day-to-day") return "day_to_day";
  if (text === "probable") return "probable";
  if (OUT_CODES.includes(text) || /-il$/.test(text)) return "out";
  return "active";
};

// src/analysis-profile.ts
var analysisProfile = (sport) => ({
  score: sport === "football" ? "goals" : sport === "mlb" ? "runs" : "points",
  normalization: sport === "football" ? 90 : sport === "nba" ? 36 : sport === "mlb" ? 9 : null,
  playerLines: sport === "nba" ? {
    points: [14.5, 19.5, 24.5],
    rebounds: [4.5, 6.5, 8.5],
    assists: [3.5, 5.5, 7.5],
    three_pointers_made: [1.5, 2.5, 3.5],
    points_rebounds_assists: [24.5, 29.5, 34.5],
    steals: [0.5],
    blocks: [0.5]
  } : sport === "nfl" ? {
    passing_yards: [199.5, 249.5],
    passing_touchdowns: [1.5, 2.5],
    rushing_yards: [39.5, 59.5, 79.5],
    receiving_yards: [39.5, 59.5, 79.5],
    receptions: [3.5, 5.5],
    touchdowns: [0.5, 1.5]
  } : sport === "mlb" ? {
    hits: [0.5, 1.5],
    home_runs: [0.5],
    rbi: [0.5],
    total_bases: [1.5, 2.5],
    pitching_strikeouts: [3.5, 5.5, 7.5]
  } : {},
  teamLines: sport === "nba" ? { points: [199.5, 209.5, 219.5, 229.5, 239.5] } : sport === "nfl" ? { points: [34.5, 39.5, 44.5, 49.5, 54.5] } : sport === "mlb" ? { runs: [6.5, 7.5, 8.5, 9.5, 10.5] } : {}
});
var playerCategories = (sport) => sport === "nba" ? {
  all: [],
  scoring: [
    "points",
    "field_goals_made",
    "field_goals_attempted",
    "three_pointers_made",
    "three_pointers_attempted",
    "free_throws_made",
    "free_throws_attempted"
  ],
  rebounding: ["rebounds", "offensive_rebounds", "defensive_rebounds"],
  creation: ["assists", "turnovers", "points_rebounds_assists"],
  defense: ["steals", "blocks", "fouls"],
  advanced: [
    "usage_percentage",
    "true_shooting_percentage",
    "effective_field_goal_percentage",
    "offensive_rating",
    "defensive_rating",
    "net_rating",
    "pace",
    "pie"
  ]
} : sport === "nfl" ? {
  all: [],
  passing: [
    "passing_yards",
    "passing_touchdowns",
    "passing_completions",
    "passing_attempts",
    "passing_interceptions"
  ],
  rushing: ["rushing_yards", "rushing_touchdowns", "rushing_attempts"],
  receiving: ["receiving_yards", "receiving_touchdowns", "receptions", "receiving_targets"],
  defense: ["total_tackles", "solo_tackles", "defensive_sacks", "defensive_interceptions"]
} : sport === "mlb" ? {
  all: [],
  batting: [
    "at_bats",
    "plate_appearances",
    "hits",
    "runs",
    "rbi",
    "home_runs",
    "total_bases",
    "walks",
    "strikeouts",
    "doubles",
    "triples",
    "stolen_bases",
    "batting_average",
    "on_base_percentage",
    "slugging"
  ],
  pitching: [
    "innings_pitched",
    "earned_run_average",
    "pitching_strikeouts",
    "pitching_walks",
    "saves"
  ]
} : {
  all: [],
  attack: [
    "goals",
    "assists",
    "shots",
    "shots_on_target",
    "shots_off_target",
    "dribbles",
    "xg",
    "big_chances_missed",
    "hit_woodwork"
  ],
  creation: [
    "passes",
    "accurate_passes",
    "key_passes",
    "crosses",
    "accurate_crosses",
    "touches",
    "big_chances_created",
    "dispossessed"
  ],
  defense: [
    "tackles",
    "tackles_won",
    "interceptions",
    "clearances",
    "blocked_shots",
    "duels",
    "duels_won",
    "aerials_won"
  ],
  discipline: [
    "fouls",
    "fouls_drawn",
    "yellow_cards",
    "red_cards",
    "offsides",
    "own_goals",
    "penalties_committed"
  ],
  goalkeeping: ["saves", "goals_conceded"]
};

// src/team-analysis.ts
var ANALYSIS_METRICS = [
  "goals",
  "corners",
  "shots",
  "shots_on_target",
  "fouls",
  "yellow_cards",
  "red_cards",
  "tackles",
  "offsides",
  "saves",
  "cards",
  "shots_inside_box",
  "goals_0_15",
  "goals_15_30",
  "goals_30_45",
  "goals_45_60",
  "goals_60_75",
  "goals_75_90"
];
var present = (value) => value !== null;
var valueOf = (game, metric, period, side) => {
  const own = game.own[period]?.[metric] ?? null;
  const opponent = game.opponent[period]?.[metric] ?? null;
  if (side === "for") return own;
  if (side === "against") return opponent;
  return own === null || opponent === null ? null : own + opponent;
};
var frequency = (key, values) => {
  const total = values.filter((value) => value !== null).length;
  const hits = values.filter((value) => value === true).length;
  return { key, hits, total, rate: total === 0 ? null : hits / total };
};
var goalCondition = (game, period, predicate, metric = "goals") => {
  const own = valueOf(game, metric, period, "for");
  const opponent = valueOf(game, metric, period, "against");
  return own === null || opponent === null ? null : predicate(own, opponent);
};
var rowsOf = (games, period, sport) => (sport === "football" ? [...ANALYSIS_METRICS] : sportDefinition(sport).teamMetrics.map((row) => row.key)).flatMap(
  (metric) => [
    "for",
    "against",
    ...sportDefinition(sport).teamMetrics.find((row) => row.key === metric)?.countable === false ? [] : ["total"]
  ].map((side) => {
    const values = games.map((game) => valueOf(game, metric, period, side));
    const available = values.filter(present);
    const average = mean(available);
    const recent = values.slice(0, 3).filter(present);
    const recentMean = mean(recent);
    const trend = values.length > 3 && recent.length === 3 && recentMean !== null && average !== null && average !== 0 ? recentMean / average - 1 : null;
    return {
      metric,
      side,
      values,
      total: available.length === 0 ? null : available.reduce((sum2, value) => sum2 + value, 0),
      mean: average,
      cv: coefficientOfVariation(available),
      median: median(available),
      p75: percentile(available, 0.75),
      sample: available.length,
      trend
    };
  })
);
var groupOf = (games, period, sport) => {
  const results = games.map((game) => {
    const won2 = goalCondition(
      game,
      "full",
      (own, opponent) => own > opponent,
      analysisProfile(sport).score
    );
    if (won2 === null) return null;
    return won2 ? "won" : goalCondition(
      game,
      "full",
      (own, opponent) => own === opponent,
      analysisProfile(sport).score
    ) ? "drawn" : "lost";
  });
  const won = results.filter((result) => result === "won").length;
  const drawn = results.filter((result) => result === "drawn").length;
  const lost = results.filter((result) => result === "lost").length;
  const scored = won + drawn + lost;
  const points = sport === "football" ? won * 3 + drawn : won;
  return {
    summary: {
      played: games.length,
      scored,
      won,
      drawn,
      lost,
      points,
      pointsRate: scored === 0 ? null : points / (scored * (sport === "football" ? 3 : 1)),
      results
    },
    rows: rowsOf(games, period, sport),
    frequencies: [
      frequency(
        "btts",
        games.map(
          (game) => goalCondition(game, period, (own, opponent) => own > 0 && opponent > 0)
        )
      ),
      frequency(
        "clean_sheet",
        games.map((game) => goalCondition(game, period, (_own, opponent) => opponent === 0))
      ),
      frequency(
        "failed_to_score",
        games.map((game) => goalCondition(game, period, (own) => own === 0))
      ),
      frequency(
        "scored_first",
        games.map(
          (game) => game.own.full?.goals === 0 && game.opponent.full?.goals === 0 ? false : game.scoredFirst
        )
      ),
      frequency(
        "won_first_half",
        games.map((game) => goalCondition(game, "first_half", (own, opponent) => own > opponent))
      )
    ]
  };
};
var MARKET_LINES = [
  { metric: "goals", period: "full", lines: [0.5, 1.5, 2.5, 3.5, 4.5] },
  { metric: "goals", period: "first_half", lines: [0.5, 1.5, 2.5] },
  { metric: "goals", period: "second_half", lines: [0.5, 1.5, 2.5] },
  { metric: "corners", period: "full", lines: [8.5, 9.5, 10.5, 11.5] },
  { metric: "corners", period: "first_half", lines: [4.5, 5.5] },
  { metric: "corners", period: "second_half", lines: [4.5, 5.5] },
  { metric: "yellow_cards", period: "full", lines: [2.5, 3.5, 4.5] }
];
var TEAM_MARKET_LINES = [
  { metric: "goals", period: "full", lines: [0.5, 1.5] },
  { metric: "corners", period: "full", lines: [3.5, 4.5, 5.5] }
];
var linesOf = (games, definitions, side, prefix) => definitions.flatMap(
  ({ metric, period, lines }) => lines.map((line) => ({
    metric,
    period,
    line,
    ...frequency(
      `${prefix}${metric}:${period}:${line}`,
      games.map((game) => {
        const value = valueOf(game, metric, period, side);
        return value === null ? null : value > line;
      })
    )
  }))
);
var pairedSums = (games, period, side, metric) => {
  let goals = 0;
  let shots = 0;
  let sample = 0;
  for (const game of games) {
    const scored = valueOf(game, "goals", period, side);
    const attempted = valueOf(game, metric, period, side);
    if (scored === null || attempted === null) continue;
    goals += scored;
    shots += attempted;
    sample += 1;
  }
  return { goals, shots, sample };
};
var efficiencyOf = (games, period, side) => {
  const shots = pairedSums(games, period, side, "shots");
  const onTarget = pairedSums(games, period, side, "shots_on_target");
  return {
    shotsPerGoal: shots.goals === 0 ? null : shots.shots / shots.goals,
    shotsOnTargetPerGoal: onTarget.goals === 0 ? null : onTarget.shots / onTarget.goals,
    conversion: shots.shots === 0 ? null : shots.goals / shots.shots,
    sample: shots.sample
  };
};
var analyzeTeam = (games, period = "full", sport = "football") => ({
  ...groupOf(games, period, sport),
  home: groupOf(
    games.filter((game) => game.side === "home"),
    period,
    sport
  ),
  away: groupOf(
    games.filter((game) => game.side === "away"),
    period,
    sport
  ),
  markets: linesOf(
    games,
    sport === "football" ? MARKET_LINES : Object.entries(analysisProfile(sport).teamLines).map(([metric, lines]) => ({
      metric,
      period: "full",
      lines: lines ?? []
    })),
    "total",
    ""
  ),
  teamMarkets: linesOf(games, sport === "football" ? TEAM_MARKET_LINES : [], "for", "for:"),
  efficiency: sport === "football" ? { for: efficiencyOf(games, period, "for"), against: efficiencyOf(games, period, "against") } : null
});

// src/match-profit.ts
var profitOf = (game, pushOnDraw) => {
  if (game.result === "won") return game.price - 1;
  if (game.result === "drawn") return pushOnDraw ? 0 : -1;
  return -1;
};
var backingProfit = (games, options) => {
  const priced = games.filter(
    (game) => game.price !== null && game.result !== null
  );
  let cumulative = 0;
  const entries = priced.map((game) => {
    const profit = profitOf(game, options.pushOnDraw);
    cumulative += profit;
    return { profit, cumulative };
  });
  const wins = priced.filter((game) => game.result === "won").length;
  return {
    entries,
    priced: priced.length,
    sample: games.length,
    total: priced.length === 0 ? null : cumulative,
    roi: priced.length === 0 ? null : cumulative / priced.length,
    averagePrice: mean(priced.map((game) => game.price)),
    averageImpliedProbability: mean(priced.map((game) => 1 / game.price)),
    winRate: priced.length === 0 ? null : wins / priced.length
  };
};
var rateOf = (games, compare) => {
  let hits = 0;
  let total = 0;
  for (const game of games) {
    if (game.ownScore === null || game.opponentScore === null || game.line === null) continue;
    const margin = compare(
      game
    );
    if (margin === 0) continue;
    total += 1;
    if (margin > 0) hits += 1;
  }
  return { hits, total, rate: total === 0 ? null : hits / total };
};
var spreadCoverRate = (games) => rateOf(games, (game) => game.ownScore + game.line - game.opponentScore);
var totalOverRate = (games) => rateOf(games, (game) => game.ownScore + game.opponentScore - game.line);

// src/player-analysis.ts
var sum = (values) => values.reduce((total, value) => total + value, 0);
var present2 = (value) => value !== null;
var LINES = [
  { metric: "goals", lines: [0.5, 1.5] },
  { metric: "assists", lines: [0.5] },
  { metric: "shots", lines: [0.5, 1.5, 2.5] },
  { metric: "shots_on_target", lines: [0.5, 1.5] },
  { metric: "tackles", lines: [0.5, 1.5, 2.5] },
  { metric: "fouls", lines: [0.5, 1.5] },
  { metric: "yellow_cards", lines: [0.5] },
  { metric: "saves", lines: [1.5, 2.5, 3.5] }
];
var analyzePlayer = (input, sport = "football") => {
  const profile = analysisProfile(sport);
  const combinations = sport === "nba" ? {
    points_rebounds_assists: ["points", "rebounds", "assists"],
    points_rebounds: ["points", "rebounds"],
    points_assists: ["points", "assists"]
  } : sport === "nfl" ? { touchdowns: ["rushing_touchdowns", "receiving_touchdowns"] } : {};
  const games = input.map((game) => {
    const values = { ...game.values };
    for (const [key, metrics] of Object.entries(combinations))
      if (metrics.every((metric) => values[metric] !== void 0))
        values[key] = sum(metrics.map((metric) => values[metric]));
    return { ...game, values };
  });
  const definitions = [
    ...new Map(
      [
        ...sportDefinition(sport).playerMetrics,
        ...Object.keys(combinations).map((key) => ({ key, countable: true }))
      ].map((definition) => [definition.key, definition])
    ).values()
  ];
  const minutes = games.flatMap(
    (game) => game.values.minutes === void 0 ? [] : [game.values.minutes]
  );
  return {
    summary: {
      appearances: games.length,
      starts: games.filter((game) => game.started === true).length,
      knownStarts: games.filter((game) => game.started !== null).length,
      minutes: sum(minutes),
      minutesSample: minutes.length
    },
    normalization: profile.normalization,
    rows: definitions.map(({ key: metric, countable }) => {
      const values = games.map((game) => game.values[metric] ?? null);
      const available = values.filter(present2);
      const average = mean(available);
      const recent = values.slice(0, 3).filter(present2);
      const recentMean = mean(recent);
      const paired = games.flatMap((game) => {
        const value = game.values[metric];
        const played = game.values[sport === "mlb" ? "innings_pitched" : "minutes"];
        return value === void 0 || played === void 0 || played <= 0 ? [] : [{ value, minutes: played }];
      });
      const normalizable = countable && metric !== "minutes" && profile.normalization !== null && (sport !== "mlb" || ["pitching_strikeouts", "pitching_walks"].includes(metric));
      return {
        metric,
        values,
        mean: average,
        total: available.length === 0 || !countable ? null : sum(available),
        sample: available.length,
        cv: coefficientOfVariation(available),
        trend: games.length > 3 && recent.length === 3 && recentMean !== null && average !== null && average !== 0 ? recentMean / average - 1 : null,
        per90: sport === "football" && normalizable && paired.length > 0 ? sum(paired.map((pair) => pair.value)) * 90 / sum(paired.map((pair) => pair.minutes)) : null,
        per90Sample: sport === "football" && normalizable ? paired.length : 0,
        normalized: normalizable && paired.length > 0 ? sum(paired.map((pair) => pair.value)) * profile.normalization / sum(paired.map((pair) => pair.minutes)) : null,
        normalizedSample: normalizable ? paired.length : 0
      };
    }),
    frequencies: (sport === "football" ? LINES : Object.entries(profile.playerLines).map(([metric, lines]) => ({
      metric,
      lines: lines ?? []
    }))).flatMap(
      ({ metric, lines }) => lines.map((line) => {
        const values = games.flatMap(
          (game) => game.values[metric] === void 0 ? [] : [game.values[metric]]
        );
        const hits = values.filter((value) => value > line).length;
        return {
          key: `${metric}:${line}`,
          metric,
          line,
          hits,
          total: values.length,
          rate: values.length === 0 ? null : hits / values.length
        };
      })
    )
  };
};

// src/football-cards.ts
var records = (value) => Array.isArray(value) ? value.filter((item) => item && typeof item === "object") : [];
var footballCards = (body, teamId, period) => {
  if (!Array.isArray(body.events) || !Array.isArray(body.lineups) || !Array.isArray(body.periods))
    return null;
  const starters = records(body.lineups).filter(
    (row) => String(row.team_id) === teamId && row.type_id === 11
  );
  if (starters.length < 11) return null;
  const active = new Set(starters.map((row) => String(row.player_id)));
  const counts = /* @__PURE__ */ new Map();
  let count = 0;
  const periods = new Map(
    records(body.periods).map((row) => [
      String(row.id),
      row.description === "1st-half" ? "first_half" : row.description === "2nd-half" ? "second_half" : null
    ])
  );
  if (![...periods.values()].some((value) => value !== null)) return null;
  const events = records(body.events).sort(
    (a, b) => Number(a.minute ?? 0) - Number(b.minute ?? 0) || Number(a.extra_minute ?? 0) - Number(b.extra_minute ?? 0) || Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)
  );
  for (const event of events) {
    if (String(event.team_id ?? event.participant_id) !== teamId || event.rescinded === true)
      continue;
    const type = event.type?.developer_name;
    if (!type) return null;
    const player = String(event.player_id);
    if (type === "SUBSTITUTION") {
      const related = String(event.related_player_id);
      const outgoing = active.has(player) ? player : related;
      const incoming = outgoing === player ? related : player;
      active.delete(outgoing);
      active.add(incoming);
      continue;
    }
    if (!["YELLOWCARD", "REDCARD", "YELLOWREDCARD"].includes(type)) continue;
    if (event.on_bench === true || event.coach_id != null || !active.has(player)) continue;
    if (!periods.has(String(event.period_id))) return null;
    const eventPeriod = periods.get(String(event.period_id));
    if (!eventPeriod) continue;
    const amount = type === "YELLOWCARD" ? 1 : 2;
    const previous = counts.get(player) ?? 0;
    const added = Math.min(amount, 3 - previous);
    counts.set(player, previous + added);
    if (period === "full" || period === eventPeriod) count += added;
    if (type !== "YELLOWCARD") active.delete(player);
  }
  return count;
};

// src/football-goal-intervals.ts
var GOAL_INTERVAL_KEYS = [
  "goals_0_15",
  "goals_15_30",
  "goals_30_45",
  "goals_45_60",
  "goals_60_75",
  "goals_75_90"
];
var GOAL_EVENTS = ["GOAL", "OWNGOAL", "PENALTY"];
var IRREGULAR_PERIODS = [
  "ext-1st-half",
  "ext-2nd-half",
  "extra-time",
  "penalties",
  "penalty-shootout"
];
var emptyIntervals = () => Object.fromEntries(GOAL_INTERVAL_KEYS.map((key) => [key, 0]));
var numberOf = (value) => {
  const parsed = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};
var intervalOf = (minute) => {
  if (minute < 1) return null;
  if (minute <= 15) return "goals_0_15";
  if (minute <= 30) return "goals_15_30";
  if (minute <= 45) return "goals_30_45";
  if (minute <= 60) return "goals_45_60";
  if (minute <= 75) return "goals_60_75";
  return "goals_75_90";
};
var currentGoalsOf = (scores, teamId) => {
  for (const row of scores)
    if (String(row.description) === "CURRENT" && String(row.participant_id) === teamId)
      return numberOf(row.score?.goals);
  return null;
};
var sideOf = (event, scored, homeId, awayId) => {
  const [home, away] = String(event.result).replace(/\s/g, "").split("-").map((part) => numberOf(part));
  if (home === scored.home + 1 && away === scored.away) return "home";
  if (away === scored.away + 1 && home === scored.home) return "away";
  const participant = String(event.participant_id);
  return participant === homeId ? "home" : participant === awayId ? "away" : null;
};
var footballGoalIntervals = (body, homeId, awayId) => {
  if (!Array.isArray(body.events) || !Array.isArray(body.periods) || !Array.isArray(body.scores))
    return null;
  const irregular = body.periods.some(
    (period) => IRREGULAR_PERIODS.includes(String(period.description).toLowerCase())
  );
  if (irregular) return null;
  const scores = body.scores;
  const expected = {
    home: currentGoalsOf(scores, homeId),
    away: currentGoalsOf(scores, awayId)
  };
  if (expected.home === null || expected.away === null) return null;
  const goals = body.events.filter(
    (event) => GOAL_EVENTS.includes(String(event.type?.developer_name)) && event.rescinded !== true
  ).sort(
    (a, b) => Number(a.sort_order) - Number(b.sort_order) || Number(a.minute) - Number(b.minute) || Number(a.extra_minute) - Number(b.extra_minute)
  );
  const intervals = { home: emptyIntervals(), away: emptyIntervals() };
  const scored = { home: 0, away: 0 };
  for (const goal of goals) {
    const side = sideOf(goal, scored, homeId, awayId);
    if (side === null) return null;
    const minute = numberOf(goal.minute);
    const interval = minute === null ? null : intervalOf(minute);
    if (interval === null) return null;
    intervals[side][interval] += 1;
    scored[side] += 1;
  }
  if (scored.home !== expected.home || scored.away !== expected.away) return null;
  return intervals;
};

// src/ticket-math.ts
var settleLine = (value, line, side) => {
  const lines = Math.abs(line * 2 % 1) > 0 ? [line - 0.25, line + 0.25] : [line];
  const outcomes = lines.map(
    (threshold) => value === threshold ? 0 : (side === "over" ? value > threshold : value < threshold) ? 1 : -1
  );
  const score = outcomes.reduce((sum2, result) => sum2 + result, 0) / outcomes.length;
  return score === 1 ? "won" : score === -1 ? "lost" : score === 0.5 ? "halfWon" : score === -0.5 ? "halfLost" : "void";
};
var resultMultiplier = (result, price) => result === "won" ? price : result === "halfWon" ? (price + 1) / 2 : result === "halfLost" ? 0.5 : result === "lost" ? 0 : 1;
var settledTicket = (items, stake, results) => {
  if (items.some((item) => results[item.id] === "lost"))
    return { status: "lost", payout: 0, profit: -stake };
  if (!items.length || items.some((item) => !results[item.id] || results[item.id] === "pending"))
    return { status: "pending", payout: null, profit: null };
  const payout = Math.round(
    stake * items.reduce((total, item) => total * resultMultiplier(results[item.id], item.price), 1) * 100
  ) / 100;
  return {
    status: items.every((item) => results[item.id] === "void") ? "void" : payout >= stake ? "won" : "lost",
    payout,
    profit: Math.round((payout - stake) * 100) / 100
  };
};
var ticketIndicators = (items) => {
  if (!items.length) return { probability: 0, edge: 0, risk: "unknown" };
  const probability = items.reduce((total, item) => total * item.probability, 1);
  const edge = items.reduce((total, item) => total * (1 + item.expectedValue), 1) - 1;
  return {
    probability,
    edge,
    risk: probability >= 0.65 ? "low" : probability >= 0.35 ? "medium" : "high"
  };
};

// src/historical-estimate.ts
var historicalEstimate = (results, price) => {
  const sample = results.length + 2;
  const wins = results.filter((result) => result === "won").length;
  const winningStake = wins + results.filter((result) => result === "halfWon").length / 2 + 1;
  const returnedStake = results.reduce(
    (total, result) => total + (result === "void" ? 1 : result === "halfWon" || result === "halfLost" ? 0.5 : 0),
    0
  );
  return {
    probability: winningStake / sample,
    pushProbability: returnedStake / sample,
    fairOdds: (sample - returnedStake) / winningStake,
    expectedValue: (results.reduce((total, result) => total + resultMultiplier(result, price), 0) + price) / sample - 1
  };
};
var playerPropResults = (values, line, side) => values.flatMap((value) => {
  if (value === null) return [];
  const result = settleLine(value, line, side);
  return result === "pending" ? [] : [result];
});
var playerPropEstimate = (values, line, side, price) => {
  const results = playerPropResults(values, line, side);
  return {
    ...historicalEstimate(results, price),
    hits: results.filter((result) => result === "won").length,
    known: results.length,
    total: values.length
  };
};

// src/player-radar.ts
var playerRadarTrend = (values, line) => {
  const known = values.filter((value) => value !== null);
  const hits = known.filter((value) => value > line).length;
  const complete = values.length > 0 && known.length === values.length;
  return {
    hits,
    known: known.length,
    total: values.length,
    missing: values.length - known.length,
    average: complete ? known.reduce((sum2, value) => sum2 + value, 0) / known.length : null,
    rate: complete ? hits / values.length : null
  };
};
var playerRadarEstimate = (values, line, price) => {
  const { probability, fairOdds, expectedValue: expectedValue2 } = historicalEstimate(
    values.flatMap(
      (value) => value === null ? [] : [value > line ? "won" : "lost"]
    ),
    price
  );
  return { probability, fairOdds, expectedValue: expectedValue2 };
};

// src/player-odds.ts
var playerNameKey = (name) => name.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
var playerOdds = (raw) => {
  if (raw.stopped === true || raw.suspended === true) return null;
  const price = typeof raw.value === "string" || typeof raw.value === "number" ? Number(raw.value) : NaN;
  const bookmaker = raw.bookmaker;
  if (!Number.isFinite(price) || price <= 1 || typeof raw.name !== "string" || !raw.name.trim() || typeof bookmaker?.name !== "string" || !bookmaker.name.trim())
    return null;
  const label = typeof raw.label === "string" ? raw.label.trim() : "";
  const market = Number(raw.market_id);
  const milestones = {
    334: "shots_on_target",
    336: "shots",
    338: "fouls",
    340: "tackles"
  };
  let metric = milestones[market];
  let line = 0.5;
  if (metric) {
    const count = /^(\d+)\+$/.exec(label);
    if (!count || Number(count[1]) < 1) return null;
    line = Number(count[1]) - 0.5;
  } else if (market === 90 && (label === "Anytime" || raw.market_description === "Anytime Goal Scorer" && label === raw.name))
    metric = "goals";
  else if (market === 331 && label === "Score") metric = "goals";
  else if (market === 332 && label === "Assist") metric = "assists";
  else if (market === 64 && label === "Booked") metric = "yellow_cards";
  else return null;
  return { playerName: raw.name, metric, line, price, bookmaker: bookmaker.name };
};

// src/selection-identity.ts
var selectionIdentity = (selection) => (selection.kind === "player" ? [
  selection.fixtureId,
  "player",
  selection.playerId,
  selection.metric,
  selection.period,
  selection.side,
  selection.line
] : [
  selection.fixtureId,
  selection.teamSide ?? "match",
  selection.market,
  selection.period,
  selection.side,
  selection.line
]).join(":");

// src/american-odds.ts
var ROUNDING = 1e4;
var americanToDecimal = (price) => {
  if (!Number.isFinite(price)) return void 0;
  const decimal = price >= 100 ? 1 + price / 100 : price <= -100 ? 1 + 100 / Math.abs(price) : void 0;
  return decimal === void 0 ? void 0 : Math.round(decimal * ROUNDING) / ROUNDING;
};

// src/leg-progress.ts
var SCORE_MARKETS = ["result", "moneyline", "spread", "btts"];
var UNBOUNDED = Number.MAX_SAFE_INTEGER;
var scoreOf = (side) => side.goals ?? side.points ?? side.runs;
var monotonicallyDecided = (value, line, side) => settleLine(value, line, side) === settleLine(UNBOUNDED, line, side);
var legValue = (item, values) => {
  if (SCORE_MARKETS.includes(item.market)) return null;
  if (item.teamSide) return values[item.teamSide][item.market] ?? null;
  const home = values.home[item.market];
  const away = values.away[item.market];
  return home != null && away != null ? home + away : null;
};
var selectionResult = (item, values) => {
  if (SCORE_MARKETS.includes(item.market)) {
    const h = scoreOf(values.home);
    const a = scoreOf(values.away);
    if (h == null || a == null) return "pending";
    if (item.market === "btts") return (h > 0 && a > 0) === (item.side === "yes") ? "won" : "lost";
    if (item.market === "spread")
      return item.line === null ? "pending" : settleLine(h - a, -item.line, item.side === "home" ? "over" : "under");
    if (h === a && item.market === "moneyline") return "void";
    const winner = h === a ? "draw" : h > a ? "home" : "away";
    return winner === item.side ? "won" : "lost";
  }
  const total = legValue(item, values);
  if (total == null || item.line === null || item.side !== "over" && item.side !== "under")
    return "pending";
  return settleLine(total, item.line, item.side);
};
var legProgress = (item, values) => {
  const result = selectionResult(item, values);
  const current = legValue(item, values);
  const decided = current !== null && item.line !== null && (item.side === "over" || item.side === "under") ? monotonicallyDecided(current, item.line, item.side) : false;
  return { current, line: item.line, provisional: result === "pending" ? null : result, decided };
};
var didNotParticipate = (values) => values.played === false || values.played !== true && values.minutes === 0;
var playerSelectionResult = (item, values) => {
  if (!values) return "pending";
  if (didNotParticipate(values)) return "void";
  if (values.value === null) return "pending";
  return settleLine(values.value, item.line, item.side);
};
var playerLegProgress = (item, values) => {
  const result = playerSelectionResult(item, values);
  const current = values?.value ?? null;
  const decided = values !== null && didNotParticipate(values) || current !== null && monotonicallyDecided(current, item.line, item.side);
  return { current, line: item.line, provisional: result === "pending" ? null : result, decided };
};

// src/lineup-status.ts
var LINEUP_STATUSES = [
  "starter",
  "bench",
  "absent",
  "expected",
  "pending",
  "unknown"
];
var LINEUP_PENDING_MS = 2 * 36e5;
var lineupStatus = ({
  sport,
  row,
  teamConfirmed,
  startsAt,
  now
}) => {
  if (row !== null) {
    if (!row.confirmed) return "expected";
    if (row.starter === true) return "starter";
    if (row.starter === false) return "bench";
    return row.battingOrder === null ? "unknown" : "starter";
  }
  if (teamConfirmed && sport === "football") return "absent";
  const untilKickoff = Date.parse(startsAt) - now;
  return untilKickoff >= 0 && untilKickoff <= LINEUP_PENDING_MS ? "pending" : "unknown";
};

// src/today.ts
var compareKickoff = (left, right) => {
  if (left === right) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return left.localeCompare(right);
};
var orderByFavourite = (items, favouriteIds) => {
  const rank = (entry) => entry.competitionId !== null && favouriteIds.has(entry.competitionId) ? 0 : 1;
  return [...items].sort(
    (left, right) => rank(left) - rank(right) || compareKickoff(left.startsAt, right.startsAt) || left.id.localeCompare(right.id)
  );
};

// src/spread-line.ts
var spreadLineFor = (side, homeLine) => side === "away" && homeLine !== 0 ? -homeLine : homeLine;
export {
  ANALYSIS_METRICS,
  AVAILABILITY_STATUSES,
  FOOTBALL,
  GOAL_INTERVAL_KEYS,
  LINEUP_PENDING_MS,
  LINEUP_STATUSES,
  MARKET_SIDES,
  METRICS,
  MLB,
  NBA,
  NFL,
  PERIODS,
  SAMPLE_SIZES,
  SPORTS,
  SPORT_DEFINITIONS,
  americanToDecimal,
  analysisProfile,
  analyzePlayer,
  analyzeTeam,
  backingProfit,
  coefficientOfVariation,
  expectedValue,
  footballCards,
  footballGoalIntervals,
  formatMarketKey,
  historicalEstimate,
  hitRate,
  isCountablePlayerMetric,
  isCountableTeamMetric,
  isPeriod,
  isPlayerMetric,
  isRejectedPropType,
  isSport,
  isTeamMetric,
  legProgress,
  legValue,
  lineupStatus,
  marketMetric,
  matchProjection,
  mean,
  median,
  milestoneLine,
  normalizeAvailability,
  orderByFavourite,
  parseMarketKey,
  percentile,
  playerCategories,
  playerLegProgress,
  playerMetricKeys,
  playerNameKey,
  playerOdds,
  playerPropEstimate,
  playerPropMetricKeys,
  playerPropResults,
  playerRadarEstimate,
  playerRadarTrend,
  playerSelectionResult,
  propMetricFor,
  radarMarketKeys,
  radarSelections,
  radarValueBet,
  resultMultiplier,
  sampleStdDev,
  selectSample,
  selectionIdentity,
  selectionResult,
  settleLine,
  settledTicket,
  sportDefinition,
  sportMarkets,
  spreadCoverRate,
  spreadLineFor,
  teamMetricKeys,
  teamProjection,
  ticketIndicators,
  totalOverRate
};
//# sourceMappingURL=index.js.map